import React, { ReactNode, ComponentProps, memo, useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import SvgIcon from '@mui/material/SvgIcon'
import { ISpeedometerProps, GradientPosition } from './Speedometer.type'

const SpeedometerPath = `M35,157.075 C1.31069,123.372 1.31069,68.7284 35,35.0251 C68.6894,1.32189 123.311,1.32189 157,35.0251 C190.689,68.7284 190.689,123.372 157,157.075`
export const Speedometer = memo((props: ISpeedometerProps) => {
  const { palette } = useTheme()

  const {
    id,
    children,
    foregroundColor = palette.success.light,
    backgroundColor = palette.primary.dark,
    accentColor = palette.error.main,
    dotColor = '#fff',
    value,
    disableDot = false,
    width = '192px',
    height = '167px',
    gradientPosition = 'END',
  } = props
  const isAtEnd = value >= 100
  const styles = {
    root: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      textAlign: 'center',
    },
    svg: {
      position: 'absolute',
      top: '0px',
      left: '0px',
      zIndex: '1',
      width: '100%',
      height: '100%',
      fill: 'none',

      '& path, & circle': {
        transition: `all 200ms ease`,
      },
    },
    foreignObject: {
      width: '100%',
      aspectRatio: '1/1',
    },
    noGradient: {
      backgroundColor: foregroundColor,
    },
    defaultGradient: {
      backgroundImage: `conic-gradient(from 142deg  at 50% 50%, ${
        isAtEnd ? accentColor : foregroundColor
      } 66%, ${accentColor} 100%)`,
    },
    reversedGradient: {
      backgroundImage: `conic-gradient(from 208deg  at 50% 50%, ${
        isAtEnd ? foregroundColor : accentColor
      } 0%, ${foregroundColor} 33%)`,
    },
  }

  const calculatedValue = Math.min(100, 100 - value)

  const GradientsBackgrounds: Record<GradientPosition, string> = {
    END: 'defaultGradient',
    NONE: 'noGradient',
    START: 'reversedGradient',
  }

  const formattedId = useMemo(
    () => id.trim().replace(/\s+/g, '-').toLowerCase(),
    [id],
  )

  return (
    <Box width={width} height={height} sx={styles.root } className="speedoMeter">
      
        <SvgIcon sx={styles.svg } viewBox="0 0 192 167" >
        <path
          stroke={backgroundColor}
          strokeWidth="19"
          strokeLinecap="round"
          d={SpeedometerPath}
        />
        <defs>
          <mask id={`${formattedId}-mask`}>
            <path
              stroke="white"
              fill="none"
              pathLength="100"
              strokeWidth="20"
              strokeDasharray="100"
              strokeDashoffset={calculatedValue}
              strokeLinecap="round"
              d={SpeedometerPath}
            />
          </mask>
        </defs>
        <foreignObject
          x="0"
          y="0"
          width="100%"
          height="100%"
          mask={`url(#${formattedId}-mask)`}
        >
          <Box
            sx={{
              ...styles.foreignObject,
              ...styles[
                GradientsBackgrounds[gradientPosition] as keyof typeof styles
              ],
            }}
          />
        </foreignObject>

        {!disableDot && (
          <circle
            r={2}
            strokeWidth={5}
            fill={dotColor}
            style={{
              offsetPath: `path('${SpeedometerPath}')`,
              offsetDistance: `calc(${value}% - 3px)`,
            }}
          />
        )}
      </SvgIcon>

      {children && (
        <Box pt={2} zIndex={2} px={5}>
          {children}
        </Box>
      )}
    </Box>
  )
})

export const SpeedometerTitle = memo(
  (props: Omit<ComponentProps<typeof Typography>, 'variant'>) => (
    <Typography
      variant="h3"
      {...props}
      component="span"
      style={{ display: 'block' }}
    />
  ),
)

export const SpeedometerDisclaimer = memo(
  (props: Omit<ComponentProps<typeof Typography>, 'variant'>) => (
    <Typography variant="overline" {...props} />
  ),
)
